import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsOfService = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-dark-bg text-white min-h-screen flex flex-col antialiased">
            <Header variant="landing" />

            <main className="flex-grow px-6 py-20">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="mb-12">
                        <button
                            onClick={() => navigate(-1)}
                            className="text-muted-gray hover:text-white transition-colors mb-6 flex items-center gap-2"
                        >
                            <span className="material-symbols-outlined text-sm">arrow_back</span>
                            <span className="text-sm">Back</span>
                        </button>
                        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Terms of Service</h1>
                        <p className="text-muted-gray text-sm">Last updated: February 4, 2025</p>
                    </div>

                    {/* Content */}
                    <div className="space-y-8 text-gray-300 leading-relaxed">
                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Agreement to Terms</h2>
                            <p>
                                By accessing and using Projekto's services, you agree to be bound by these Terms of Service.
                                If you do not agree to these terms, please do not use our services.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Services Provided</h2>
                            <p className="mb-3">Projekto provides custom project development services, including but not limited to:</p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Web development projects</li>
                                <li>AI/ML implementations</li>
                                <li>Deep tech solutions</li>
                                <li>Final-year academic projects</li>
                                <li>Custom software development</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Project Submission and Payment</h2>

                            <h3 className="text-white text-lg font-semibold mb-2 mt-4">Submission Process</h3>
                            <p className="mb-3">
                                When you submit a project request, you agree to provide accurate and complete information about
                                your requirements. We reserve the right to reject any project request at our discretion.
                            </p>

                            <h3 className="text-white text-lg font-semibold mb-2 mt-4">Payment Terms</h3>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>All payments are processed securely through Razorpay</li>
                                <li>The base project fee is ₹2,500 (subject to coupon discounts)</li>
                                <li>Payment is required upfront before project commencement</li>
                                <li>Additional costs may apply based on project complexity and scope</li>
                            </ul>

                            <h3 className="text-white text-lg font-semibold mb-2 mt-4">Refund Policy</h3>
                            <p>
                                Refunds are handled on a case-by-case basis. Once project development has commenced,
                                refunds may not be available. Please contact us at projecktocare@gmail.com to discuss refund requests.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Project Timeline and Delivery</h2>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>We will review your project within 24-48 hours of submission</li>
                                <li>Project timelines will be communicated during the initial consultation</li>
                                <li>Timelines may vary based on project complexity and scope</li>
                                <li>Delays caused by incomplete information or client unavailability may extend delivery times</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Intellectual Property</h2>

                            <h3 className="text-white text-lg font-semibold mb-2 mt-4">Client Ownership</h3>
                            <p className="mb-3">
                                Upon full payment and project completion, you will own the final deliverables and code created
                                specifically for your project.
                            </p>

                            <h3 className="text-white text-lg font-semibold mb-2 mt-4">Our Rights</h3>
                            <p className="mb-3">We retain the right to:</p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Use general knowledge, skills, and techniques gained during project development</li>
                                <li>Showcase completed projects in our portfolio (with your permission)</li>
                                <li>Reuse non-proprietary code components and frameworks</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Client Responsibilities</h2>
                            <p className="mb-3">As a client, you agree to:</p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Provide accurate and complete project requirements</li>
                                <li>Respond to our communications in a timely manner</li>
                                <li>Provide necessary access, credentials, and resources when required</li>
                                <li>Review deliverables and provide feedback within agreed timeframes</li>
                                <li>Not use our services for illegal or unethical purposes</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Confidentiality</h2>
                            <p>
                                We respect the confidentiality of your project information. We will not disclose your proprietary
                                information to third parties without your consent, except as required by law or as necessary to
                                complete your project.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Warranties and Disclaimers</h2>

                            <h3 className="text-white text-lg font-semibold mb-2 mt-4">Service Warranty</h3>
                            <p className="mb-3">
                                We strive to deliver high-quality work that meets your requirements. We will make reasonable efforts
                                to fix bugs and issues discovered within 30 days of project delivery.
                            </p>

                            <h3 className="text-white text-lg font-semibold mb-2 mt-4">Disclaimer</h3>
                            <p>
                                Our services are provided "as is" without warranties of any kind, either express or implied.
                                We do not guarantee that our services will be uninterrupted, error-free, or meet all your expectations.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Limitation of Liability</h2>
                            <p>
                                To the maximum extent permitted by law, Projekto shall not be liable for any indirect, incidental,
                                special, consequential, or punitive damages arising from your use of our services. Our total liability
                                shall not exceed the amount you paid for the specific project.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Termination</h2>
                            <p className="mb-3">
                                We reserve the right to terminate or suspend services if you:
                            </p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Violate these Terms of Service</li>
                                <li>Provide false or misleading information</li>
                                <li>Engage in abusive or harassing behavior</li>
                                <li>Fail to make required payments</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Modifications to Terms</h2>
                            <p>
                                We reserve the right to modify these Terms of Service at any time. Changes will be effective
                                immediately upon posting. Your continued use of our services after changes constitutes acceptance
                                of the modified terms.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Governing Law</h2>
                            <p>
                                These Terms of Service shall be governed by and construed in accordance with the laws of India.
                                Any disputes shall be subject to the exclusive jurisdiction of the courts in India.
                            </p>
                        </section>

                        <section className="border-t border-thin-divider pt-8">
                            <h2 className="text-white text-2xl font-bold mb-4">Contact Us</h2>
                            <p className="mb-3">
                                If you have questions about these Terms of Service, please contact us:
                            </p>
                            <div className="bg-dark-card border border-border-gray rounded-xl p-6">
                                <p className="text-white font-semibold mb-2">Projekto Support</p>
                                <a href="mailto:projecktocare@gmail.com" className="text-blue-400 hover:text-blue-300 transition-colors">
                                    projecktocare@gmail.com
                                </a>
                            </div>
                        </section>
                    </div>
                </div>
            </main>

            <Footer variant="landing" />
        </div>
    );
};

export default TermsOfService;
